import { z } from "zod";
import { FeedObjectInfo, NeoFeed, ObjectInfo } from "./types";
import {
  Chart,
  Colors,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  TimeScale,
  TimeSeriesScale,
  Tooltip,
} from "chart.js";
import "chartjs-adapter-luxon";

function clear_div(div: HTMLDivElement) {
  div.innerHTML = "";
}

type ROW_CALLBACK = (id: number) => undefined;

export function display_feed(div: HTMLDivElement, feed: z.infer<typeof NeoFeed>, callback: ROW_CALLBACK) {
  clear_div(div);
  const near_objects = feed.near_earth_objects;
  for (const [date, objects] of Object.entries(near_objects)) {
    const details = document.createElement("details");
    const summary = document.createElement("summary");
    summary.innerText = date;
    details.appendChild(summary);
    for (const object of objects) {
      const table = document.createElement("table");
      const tbody = table_header(table);
      tbody.appendChild(object_row(object, callback));
      details.appendChild(table);
      details.appendChild(close_approach_table(object));
      details.appendChild(document.createElement("hr"));
    }
    div.appendChild(details);
  }
}

function close_approach_table(data: z.infer<typeof FeedObjectInfo>): HTMLTableElement {
  const number_formatter = Intl.NumberFormat();
  const table = document.createElement("table");
  const thead = document.createElement("thead");
  const headers = ["Date", "Rel. Velocity (km)", "Miss Distance (km)"];
  populate_table_header(thead, headers);
  table.appendChild(thead);

  const formatter = Intl.DateTimeFormat(undefined, { dateStyle: "short", timeStyle: "short" });

  const tbody = document.createElement("tbody");
  const close_approaches = data.close_approach_data;
  for (const approach of close_approaches) {
    const tr = document.createElement("tr");
    const th = document.createElement("th");
    th.innerText = formatter.format(approach.close_approach_date_full);
    th.scope = "col";
    tr.appendChild(th);

    let td = document.createElement("td");
    td.innerText = number_formatter.format(approach.relative_velocity.kilometers_per_hour);
    tr.appendChild(td);

    td = document.createElement("td");
    td.innerText = number_formatter.format(approach.miss_distance.kilometers);
    tr.appendChild(td);
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  return table;
}

export function display_lookup(div: HTMLDivElement, data: z.infer<typeof ObjectInfo>) {
  clear_div(div);
  const table = document.createElement("table");
  const tbody = table_header(table);
  tbody.appendChild(object_row(data));
  div.appendChild(table);

  const canvas = document.createElement("canvas");
  Chart.register(TimeScale, TimeSeriesScale, LineController, LinearScale, PointElement, LineElement, Colors, Tooltip);
  new Chart(canvas, {
    type: "line",
    data: {
      labels: data.close_approach_data.map((approach) => approach.close_approach_date_full),
      datasets: [
        {
          label: "Miss Distance",
          data: data.close_approach_data.map((approach) => approach.miss_distance.kilometers),
        },
      ],
    },
    options: {
      scales: {
        x: {
          type: "time",
          time: {
            unit: "year",
          },
        },
      },
      plugins: {
        tooltip: {
          position: "average",
          intersect: false,
          callbacks: {
            label: (context) => {
              let label = context.dataset.label || "";
              if (label) {
                label += ": ";
              }
              if (context.parsed.y !== null) {
                label += new Intl.NumberFormat(undefined, { style: "unit", unit: "kilometer" }).format(
                  context.parsed.y,
                );
              }
              return label;
            },
          },
        },
      },
    },
  });
  div.appendChild(canvas);

  div.appendChild(close_approach_table(data));
}

function table_header(table: HTMLTableElement): HTMLTableSectionElement {
  const thead = document.createElement("thead");
  const headers = ["ID", "Name", "Abs. Magnitude", "Est. Diameter (km)", "Is hazardous?", "Is sentry?"];
  populate_table_header(thead, headers);
  table.appendChild(thead);
  const tbody = document.createElement("tbody");
  table.appendChild(tbody);
  return tbody;
}

function populate_table_header(thead: HTMLTableSectionElement, headers: string[]) {
  const tr = document.createElement("tr");
  for (const header of headers) {
    const th = document.createElement("th");
    th.innerText = header;
    th.scope = "col";
    tr.appendChild(th);
  }
  thead.appendChild(tr);
}

function object_row(
  data: z.infer<typeof FeedObjectInfo>,
  callback: ROW_CALLBACK | undefined = undefined,
): HTMLTableRowElement {
  const tr = document.createElement("tr");

  const row_data: string[] = [
    data.name,
    data.absolute_magnitude_h.toString(),
    `${data.estimated_diameter.kilometers.estimated_diameter_min}-${data.estimated_diameter.kilometers.estimated_diameter_max}`,
    data.is_potentially_hazardous_asteroid ? "Yes" : "No",
    data.is_sentry_object ? "Yes" : "No",
  ];

  const th = document.createElement("th");
  th.scope = "row";
  if (callback) {
    const a = document.createElement("a");
    a.href = "#lookup-form";
    a.addEventListener("click", () => callback(data.neo_reference_id));
    a.innerText = data.neo_reference_id.toString();
    th.appendChild(a);
  } else {
    th.innerText = data.neo_reference_id.toString();
  }
  tr.appendChild(th);
  for (const val of row_data) {
    const td = document.createElement("td");
    td.innerText = val;
    tr.appendChild(td);
  }

  return tr;
}
